import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  DateRangeFormComponent,
  MOMENT_DATEPICKER_PROVIDERS,
  NgCommonModule,
  validFormGroupChanges$,
} from '@principle-theorem/ng-shared';
import {
  DATE_FILTER_COMPARISONS,
  DATE_RANGE_COMPARISONS,
  DateFilterType,
  DYNAMIC_DATE_COMPARISONS,
  ICustomReportFilter,
  ICustomReportFilterValue,
  IRelativeDateOffset,
  ReportFilterComparison,
  STATIC_DATE_COMPARISONS,
  TimingDirection,
  TimingUnit,
} from '@principle-theorem/principle-core/interfaces';
import {
  CustomReportFilter,
  getDateFilterLabel,
  ReportBuilderDataSourceInstance,
} from '@principle-theorem/reporting';
import {
  AtLeast,
  debounceUserInput,
  isISODateType,
  toISODate,
  toMoment,
} from '@principle-theorem/shared';
import { first, isArray } from 'lodash';
import * as moment from 'moment-timezone';
import { Moment } from 'moment-timezone';
import { distinctUntilChanged, map, take } from 'rxjs/operators';
import {
  EditReportFilterDateForm,
  IDateFilterForm,
} from './edit-report-filter-date-form';

@Component({
  selector: 'pr-edit-report-filter-date',
  templateUrl: './edit-report-filter-date.component.html',
  styleUrls: ['./edit-report-filter-date.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DateRangeFormComponent,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInput,
    MatSelectModule,
    NgCommonModule,
    ReactiveFormsModule,
  ],
  providers: [...MOMENT_DATEPICKER_PROVIDERS],
})
export class EditReportFilterDateComponent {
  formGroup = new EditReportFilterDateForm();
  comparisons = CustomReportFilter.getComparisonOptions([
    ReportFilterComparison.DateIs,
    ReportFilterComparison.DateIsBefore,
    ReportFilterComparison.DateIsAfter,
    ReportFilterComparison.DateIsBetween,
    ReportFilterComparison.DateIsAtLeast,
    ReportFilterComparison.DateIsAtMost,
  ]);
  timingDirections = [TimingDirection.Before, TimingDirection.After];
  timingUnits = [
    TimingUnit.Days,
    TimingUnit.Weeks,
    TimingUnit.Months,
    TimingUnit.Years,
  ];

  dataSource = input.required<ReportBuilderDataSourceInstance>();
  filter = input.required<ICustomReportFilter>();
  filterChange = output<Partial<ICustomReportFilter>>();

  constructor() {
    toObservable(this.filter)
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((filter) => this._initializeForm(filter));

    validFormGroupChanges$(this.formGroup)
      .pipe(
        map(({ comparison, dateType }) => ({ comparison, dateType })),
        distinctUntilChanged(),
        takeUntilDestroyed()
      )
      .subscribe(({ comparison, dateType }) => {
        this.formGroup.syncState(comparison, dateType);
      });

    validFormGroupChanges$(this.formGroup)
      .pipe(
        debounceUserInput(),
        map((value) => this._fromFormData(value)),
        takeUntilDestroyed()
      )
      .subscribe((filterChanges) => this.filterChange.emit(filterChanges));
  }

  dateFromChange(dateFrom?: Moment): void {
    if (!dateFrom || !dateFrom.isValid()) {
      return;
    }
    const current = this.formGroup.value.dateFrom;
    const hasChanged = !current.isSame(dateFrom, 'day');
    this.formGroup.patchValue({ dateFrom }, { emitEvent: hasChanged });
  }

  dateToChange(dateTo?: Moment): void {
    if (!dateTo || !dateTo.isValid()) {
      return;
    }
    const current = this.formGroup.value.dateTo;
    const hasChanged = !current.isSame(dateTo, 'day');
    this.formGroup.patchValue({ dateTo }, { emitEvent: hasChanged });
  }

  private _toFormData(
    filter: ICustomReportFilter
  ): AtLeast<IDateFilterForm, 'comparison' | 'dateType'> {
    const rawComparison = CustomReportFilter.getComparison(filter);
    const comparison = DATE_FILTER_COMPARISONS.includes(rawComparison)
      ? rawComparison
      : ReportFilterComparison.DateIsBetween;
    const dateType = filter.dateType ?? DateFilterType.ReportDate;

    const value = first(filter.values)?.value;

    if (STATIC_DATE_COMPARISONS.includes(comparison)) {
      const date = isISODateType(value) ? toMoment(value) : moment();
      return { comparison, dateType, date };
    }

    if (DATE_RANGE_COMPARISONS.includes(comparison)) {
      const [from, to] = isArray(value) ? value : [];
      const dateFrom = from ? moment(from) : moment();
      const dateTo = to ? moment(to) : moment();
      return { comparison, dateType, dateFrom, dateTo };
    }

    if (DYNAMIC_DATE_COMPARISONS.includes(comparison)) {
      return {
        comparison,
        dateType,
        amount: filter.relativeOffset?.amount ?? 1,
        unit: filter.relativeOffset?.unit ?? TimingUnit.Days,
        direction: filter.relativeOffset?.direction ?? TimingDirection.Before,
      };
    }

    return { comparison, dateType };
  }

  private _fromFormData(
    formData: IDateFilterForm
  ): Partial<ICustomReportFilter> {
    return {
      comparison: formData.comparison,
      values: this._getFormValues(formData),
      dateType: this._getDateType(formData),
      relativeOffset: this._getRelativeOffset(formData),
    };
  }

  private _getFormValues(
    formData: IDateFilterForm
  ): ICustomReportFilterValue[] {
    if (STATIC_DATE_COMPARISONS.includes(formData.comparison)) {
      return [
        {
          label: getDateFilterLabel(formData.date),
          value: toISODate(formData.date),
        },
      ];
    }
    if (DATE_RANGE_COMPARISONS.includes(formData.comparison)) {
      return [
        {
          label: getDateFilterLabel(formData.dateFrom, formData.dateTo),
          value: [formData.dateFrom.format(), formData.dateTo.format()],
        },
      ];
    }
    return [];
  }

  private _getDateType(formData: IDateFilterForm): DateFilterType {
    if (DYNAMIC_DATE_COMPARISONS.includes(formData.comparison)) {
      return DateFilterType.ReportDate;
    }
    return formData.dateType;
  }

  private _getRelativeOffset(
    formData: IDateFilterForm
  ): IRelativeDateOffset | undefined {
    if (!DYNAMIC_DATE_COMPARISONS.includes(formData.comparison)) {
      return;
    }
    return {
      direction: formData.direction ?? TimingDirection.Before,
      amount: formData.amount ?? 0,
      unit: formData.unit ?? TimingUnit.Days,
    };
  }

  private _initializeForm(filter: ICustomReportFilter): void {
    const formData = this._toFormData(filter);
    this.formGroup.patchValue(formData, { emitEvent: true });
    this.formGroup.syncState(formData.comparison, formData.dateType);
  }
}
